import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { InventoryLocationSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/** Where things live — a storage room, the sanctuary, the kitchen. */
export function LocationsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: ({ signal }) => inventoryApi.listLocations({ limit: 100 }, signal),
  });

  const locations = query.data?.data ?? [];

  return (
    <div className="page">
      <Link to="/inventory" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <h1 className="page-title">{t('inventory.location.title')}</h1>
        {can('inventory.location.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('inventory.location.create')}
          </button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && locations.length === 0 && (
        <EmptyState title={t('inventory.location.empty')} hint={t('inventory.location.create')} />
      )}

      {locations.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.location.singular')}</th>
                <th scope="col">{t('inventory.item.title')}</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <LocationRow key={location.id} location={location} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <LocationForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function LocationRow({ location }: { location: InventoryLocationSummaryResponse }) {
  const { t } = useI18n();
  return (
    <tr data-archived={location.archivedAt ? true : undefined}>
      <td>{location.name}</td>
      <td dir="ltr">{t('inventory.item.count', { count: location.itemCount })}</td>
    </tr>
  );
}

function LocationForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () => inventoryApi.createLocation({ name: name.trim() }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="location-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="location-form-title">{t('inventory.location.create')}</h2>
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('inventory.location.name')}
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!name.trim()}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
